import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import useIsInViewport from "../../hooks/useIsInViewport";
import { withALErrorBoundary } from "../../helpers/ErrorBoundary/ALErrorBoundary";
import { ALButton, ALLink, ALSkeleton } from "../ALComponents";
import { ComponentType } from "../../constants/ComponentType";
import IconHeart from "../icons/svgs/heart.svg";

import * as Styles from "./ugcVideos/styles.module.scss";

function UgcVideos({ content, componentType }) {
  const [activeVideo, setActiveVideo] = useState(null);
  const [isVideosLoaded, setIsVideosLoaded] = useState(false);
  const videoContainerRef = useRef(null);
  const videoRefs = useRef({});

  const isInViewport = useIsInViewport(videoContainerRef);

  const additionalClassName = componentType ? `${Styles[componentType.toLowerCase()]}` : "";

  useEffect(() => {
    if (isInViewport && !isVideosLoaded) {
      setIsVideosLoaded(true);
    }

    // Reset active video when leaving viewport (pause video)
    if (!isInViewport && isVideosLoaded) {
      setActiveVideo(null);
    }
  }, [isVideosLoaded, isInViewport]);

  return (
    <div className={`${Styles.container} ${additionalClassName}`}>
      <h2 className="h2 h2--bold h2--uc">{content.title}</h2>

      <div className={Styles.grid} ref={videoContainerRef}>
        {content?.ugcVideos.map((video, i) => (
          <div key={`${video.name}_1`} className={Styles.videoGridElement}>
            {isVideosLoaded ? (
              <div className={Styles.video}>
                <video
                  ref={(el) => (videoRefs.current[video.video.url] = el)}
                  src={video.video.url} // Ensure you have a valid video URL
                  autoPlay={i === 0}
                  style={{ width: "100%", height: "auto" }}
                  onPlay={() => setActiveVideo(video.video.url)}
                >
                  <track kind="captions" />
                  Your browser does not support the video tag.
                </video>

                {activeVideo !== video.video.url ? (
                  <button
                    className={Styles.customPlayButton}
                    onClick={() => {
                      setActiveVideo(video.video.url);
                      videoRefs.current[video.video.url]?.play(); // Ensure the video starts playing
                    }}
                    type="button"
                    aria-label="Play video"
                  >
                    <span className={Styles.customPlayButtonTriangle}>&nbsp;</span>
                  </button>
                ) : (
                  <div
                    className={Styles.customPauseButton}
                    onClick={() => {
                      if (activeVideo === video.video.url) {
                        videoRefs.current[video.video.url]?.pause(); // Pause the video element
                        setActiveVideo(null);
                      }
                    }}
                  >
                    &nbsp;
                  </div>
                )}

                <span className={`${Styles.user} subtext subtext--bold`}>{video.userHandle}</span>
                <span className={Styles.likesContainer}>
                  <IconHeart width="30" fill="#0B0F0E" stroke="#2D2927" />
                  <span className="microtext mt-[5px]">{video.likeCount}</span>
                </span>
              </div>
            ) : (
              <ALSkeleton />
            )}

            <ALLink to={video.buttonAction}>
              <ALButton variant="secondary" size="medium" fullWidth className="mt-[20px]">
                {video.buttonText}
              </ALButton>
            </ALLink>
          </div>
        ))}
      </div>
    </div>
  );
}

UgcVideos.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    ugcVideos: PropTypes.arrayOf(
      PropTypes.shape({
        userHandle: PropTypes.string,
        likeCount: PropTypes.string,
        buttonText: PropTypes.string,
        buttonAction: PropTypes.string,
        video: {
          filename: PropTypes.string,
          url: PropTypes.string,
        },
      })
    ),
  }),
  componentType: PropTypes.oneOf([ComponentType.PDP, ComponentType.HP]),
};

export default withALErrorBoundary({
  name: "UgcVideos",
  priority: "P2",
})(UgcVideos);
